// RequestKeyForm.jsx - Form to submit API key request
import React, { useState } from 'react';
import { useDeveloperAuth } from '../../contexts/DeveloperAuthContext';

function RequestKeyForm({ hasPendingRequest, onSuccess }) {
  const { authFetch } = useDeveloperAuth();

  const [formData, setFormData] = useState({
    purpose: '',
    expectedUsage: '',
    useCase: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const response = await authFetch('/api/developer/request-key', {
        method: 'POST',
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.message || 'Failed to submit request');
        return;
      }

      setSuccess(true);
      if (onSuccess) {
        setTimeout(onSuccess, 2000);
      }
    } catch (err) {
      setError(err.message || 'Failed to submit request');
    } finally {
      setLoading(false);
    }
  };

  // Show pending request message
  if (hasPendingRequest) {
    return (
      <div className="max-w-xl">
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-8 text-center">
          <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center mx-auto mb-4">
            <svg className="w-6 h-6 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-amber-900 mb-2">Request Pending</h3>
          <p className="text-amber-700">
            You already have a pending API key request. Please wait for it to be reviewed by our team.
            You'll receive an email once it's processed.
          </p>
        </div>
      </div>
    );
  }

  // Show success message
  if (success) {
    return (
      <div className="max-w-xl">
        <div className="bg-green-50 border border-green-200 rounded-2xl p-8 text-center">
          <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center mx-auto mb-4">
            <svg className="w-6 h-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-green-900 mb-2">Request Submitted</h3>
          <p className="text-green-700">
            Your API key request has been submitted successfully. We'll review it and get back to you soon.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-xl">
      <div className="bg-white rounded-2xl border border-gray-200 p-8">
        <h2 className="text-xl font-bold text-gray-900 mb-2">Request API Access</h2>
        <p className="text-gray-600 mb-6">
          Tell us about your project and how you plan to use the MySkinAI API.
        </p>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Purpose */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Purpose of API Access *
            </label>
            <textarea
              value={formData.purpose}
              onChange={(e) => setFormData({ ...formData, purpose: e.target.value })}
              className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-900 resize-none"
              rows={4}
              placeholder="Describe what you'll be building with the API (minimum 20 characters)"
              required
              minLength={20}
            />
            <p className="text-xs text-gray-500 mt-1">
              Be specific about your use case and target audience
            </p>
          </div>

          {/* Expected Usage */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Expected Usage Volume
            </label>
            <select
              value={formData.expectedUsage}
              onChange={(e) => setFormData({ ...formData, expectedUsage: e.target.value })}
              className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-900"
            >
              <option value="">Select expected volume</option>
              <option value="low">Low (less than 100 requests/day)</option>
              <option value="medium">Medium (100-1,000 requests/day)</option>
              <option value="high">High (1,000-10,000 requests/day)</option>
              <option value="enterprise">Enterprise (10,000+ requests/day)</option>
            </select>
          </div>

          {/* Use Case */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Use Case Category
            </label>
            <select
              value={formData.useCase}
              onChange={(e) => setFormData({ ...formData, useCase: e.target.value })}
              className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-900"
            >
              <option value="">Select use case</option>
              <option value="healthcare">Healthcare / Telemedicine</option>
              <option value="skincare">Skincare / Beauty Tech</option>
              <option value="research">Research / Academic</option>
              <option value="wellness">Wellness App</option>
              <option value="other">Other</option>
            </select>
          </div>

          {/* Error Message */}
          {error && (
            <div className="p-4 bg-red-50 border border-red-100 rounded-xl">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          {/* Terms Notice */}
          <div className="p-4 bg-gray-50 rounded-xl">
            <p className="text-xs text-gray-600">
              By submitting this request, you agree to our{' '}
              <a href="/terms" className="text-gray-900 underline">Terms of Service</a>
              {' '}and{' '}
              <a href="/privacy" className="text-gray-900 underline">Privacy Policy</a>.
              API access is subject to approval and rate limits.
            </p>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading || formData.purpose.length < 20}
            className="w-full py-3 px-4 bg-gray-900 text-white rounded-xl font-medium hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
          >
            {loading ? (
              <span className="inline-flex items-center">
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Submitting...
              </span>
            ) : (
              'Submit Request'
            )}
          </button>
        </form>
      </div>
    </div>
  );
}

export default RequestKeyForm;
